
import { Component, Vue, Prop } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'auth';

@Component<RestaurantSelector>({
	components: {
		TextField
	}
})
export default class RestaurantSelector extends Vue {
	@Getter('getRestaurants', { namespace }) private restaurants!: Tenant[];
	@Getter('getTenantId', { namespace }) private tenantId!: string | null;
	@Prop({ type: Array, default: () => [] }) private value!: string[];
	@Prop({ type: String, default: 'restaurant-selector' }) private id!: string;
	@Prop({ type: String, default: 'Select restaurants' }) private label!: string;
	@Prop({ type: String, default: '' }) private errorMessage!: string;
	@Prop({ type: Boolean, default: true }) private preselectCurrentLocation!: boolean;
	@Prop({ type: Boolean, default: false }) private limitSelectAllToSearch!: boolean;

	private searchString: string = '';

	$refs!: {
		searchInput: any;
	};

	private get selectedRestaurantIds(): string[] {
		return this.value;
	}

	private set selectedRestaurantIds(value: string[]) {
		this.$emit('input', value);
		this.$refs.searchInput.isFocused = value.length > 0;
	}

	/**
	 * Filter the list of restaurants based on the search string,
	 * while ensuring the current location is first.
	 *
	 * @return {Tenant[]}
	 */
	private get filteredRestaurants(): Tenant[] {
		const searchString = this.searchString.toLowerCase();
		return this.restaurants
			.filter(restaurant => restaurant.name.toLowerCase().includes(searchString))
			.sort((a, b) => {
				if (a.id === this.tenantId) {
					return -1;
				}
				if (b.id === this.tenantId) {
					return 1;
				}
				const aName = a.name.toLocaleLowerCase();
				const bName = b.name.toLocaleLowerCase();
				return aName.localeCompare(bName);
			});
	}

	/**
	 * The locations the select/unselect all button applies to: every location, or only the ones the
	 * search is currently showing when the consumer scopes it.
	 *
	 * @return {Tenant[]}
	 */
	private get selectAllTargets(): Tenant[] {
		return this.limitSelectAllToSearch ? this.filteredRestaurants : this.restaurants;
	}

	/**
	 * True once every location the button applies to is selected, so the button offers the inverse.
	 *
	 * @return {boolean}
	 */
	private get areSelectAllTargetsSelected(): boolean {
		return !!this.selectAllTargets.length
			&& this.selectAllTargets.every((restaurant: Tenant) => this.selectedRestaurantIds.includes(restaurant.id));
	}

	private mounted(): void {
		// Pre-select the current location
		if (this.preselectCurrentLocation && this.tenantId) {
			this.selectedRestaurantIds = [this.tenantId];
		}
	}

	/**
	 * Select/unselect all restaurants in the list
	 *
	 * @return {void}
	 */
	private toggleAllRestaurantsSelected(): void {
		const targetIds: string[] = this.selectAllTargets.map((restaurant: Tenant) => restaurant.id);

		if (this.limitSelectAllToSearch) {
			// Locations filtered out are left as they are, so searching then selecting adds to the
			// selection rather than replacing it.
			if (this.areSelectAllTargetsSelected) {
				const targets: Set<string> = new Set(targetIds);
				this.selectedRestaurantIds = this.selectedRestaurantIds.filter((id: string) => !targets.has(id));
			}
			else {
				this.selectedRestaurantIds = [...new Set([...this.selectedRestaurantIds, ...targetIds])];
			}
		}
		else if (this.selectedRestaurantIds.length === this.restaurants.length) {
			this.selectedRestaurantIds = [];
		}
		else {
			this.selectedRestaurantIds = targetIds;
		}
		this.$refs.searchInput.isFocused = true;
		this.$emit('clear-error');
	}

	/**
	 * Add/remove a restaurant from the selected restaurant list
	 *
	 * @param {string} id
	 * @return {void}
	 */
	private handleRestaurantSelect(id: string): void {
		const currentSelection = [...this.selectedRestaurantIds];
		const index = currentSelection.indexOf(id);

		if (index > -1) {
			currentSelection.splice(index, 1);
		}
		else {
			currentSelection.push(id);
		}

		this.selectedRestaurantIds = currentSelection;
		this.$emit('clear-error');
	}
}
